/**
 * Read-only properties of a share, a folder or a file (plan D13, Task 7.16) — a dialog with the S3 properties panel's
 * look (a label / value grid). A share reads `azure-storage:share-properties` (+ recoverability), a folder or file
 * `azure-storage:file-properties`; where the gate refuses the read (a share SAS cannot read share properties) the
 * sentence shows and nothing is sent. Quota changes go through the Quota dialog. One `properties_open` event per open:
 * `feature:storage_share_action` for a share, `feature:storage_file_action` for a folder or file.
 */
import { useEffect, useRef } from 'react';
import { Button } from '../../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../components/ui/dialog';
import type { FileServiceRef, FileShareProtocolState, StorageOperationError } from '../../../../types/azureStorage';
import { ShareErrorNotice } from './dialogs/ShareErrorNotice';
import { itemPropertyRows, sharePropertyRows, type PropertyRow } from './dialogs/filePropertyRows';
import { useShareRefusal } from './dialogs/shareGate';
import { useShareRead } from './dialogs/useShareRead';
import type { ShareDialogRequest } from './fileDialogStore';
import { startShareRequest } from './shareApi';
import { shareActionOutcome, trackFileAction, trackShareAction, type ShareActionOutcome } from './shareActions';

type Props = { request: ShareDialogRequest; onClose: () => void };
type Answer = { rows: PropertyRow[]; protocol: FileShareProtocolState };

function usePropertiesRead(request: ShareDialogRequest, resource: FileServiceRef, refusal: string | undefined) {
  const { connectionId } = request;
  const share = useShareRead({
    connectionId, resource, purpose: 'properties', enabled: !refusal && resource.kind === 'share',
    start: () => startShareRequest('shareProperties', { connectionId, resource: resource as Extract<FileServiceRef, { kind: 'share' }> }),
  });
  const item = useShareRead({
    connectionId, resource, purpose: 'item-properties', enabled: !refusal && resource.kind !== 'share',
    start: () => startShareRequest('fileProperties', { connectionId, resource: resource as Exclude<FileServiceRef, { kind: 'share' }> }),
  });
  const answer: Answer | undefined = share.data
    ? { rows: sharePropertyRows(share.data.properties, share.data.recoverability), protocol: share.data.properties.protocol }
    : item.data ? { rows: itemPropertyRows(item.data), protocol: item.data.protocol } : undefined;
  return { answer, error: share.error ?? item.error, loading: share.isLoading || item.isLoading };
}

/** Fires the open's one event when the read settles — or `refused` / `cancelled` when it never can. */
function useOpenEvent(request: ShareDialogRequest, isShare: boolean, settled: { outcome?: ShareActionOutcome; protocol: FileShareProtocolState }) {
  const fired = useRef(false);
  const fire = (outcome: ShareActionOutcome, protocol: FileShareProtocolState) => {
    if (fired.current) return;
    fired.current = true;
    const meta = { source: request.source, protocol };
    if (isShare) trackShareAction('properties_open', outcome, meta);
    else trackFileAction('properties_open', outcome, meta);
  };
  const latest = useRef(settled);
  latest.current = settled;
  const mounted = useRef(false);
  useEffect(() => { if (settled.outcome) fire(settled.outcome, settled.protocol); });
  // A real close before the answer is `cancelled`; StrictMode's simulated unmount remounts within the same task.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      queueMicrotask(() => { if (!mounted.current) fire('cancelled', latest.current.protocol); });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

export function FilePropertiesPanel({ request, onClose }: Props) {
  const resource = (request.resource ?? { kind: 'share', share: '' }) as FileServiceRef;
  const isShare = resource.kind === 'share';
  const refusal = useShareRefusal(request.connectionId)(isShare ? 'share-properties' : 'item-properties', { snapshot: resource.snapshot });
  const { answer, error, loading } = usePropertiesRead(request, resource, refusal);
  const failure: StorageOperationError | undefined = error;
  const outcome: ShareActionOutcome | undefined = refusal ? 'refused' : answer ? 'ok' : failure ? shareActionOutcome(failure) : undefined;
  useOpenEvent(request, isShare, { outcome, protocol: answer?.protocol ?? request.protocol ?? 'unknown' });
  const title = isShare ? resource.share : resource.path.split('/').pop() || resource.share;
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="file-properties-dialog">
        <DialogHeader>
          <DialogTitle>Properties of {title}</DialogTitle>
          <DialogDescription>{isShare ? 'File share' : resource.kind === 'directory' ? 'Folder' : 'File'} · read-only</DialogDescription>
        </DialogHeader>
        {refusal && <p className="text-sm text-muted-foreground" data-testid="file-properties-refusal">{refusal}</p>}
        {loading && <p className="text-sm text-muted-foreground" data-testid="file-properties-loading">Loading properties…</p>}
        {failure && <ShareErrorNotice error={failure} testId="file-properties-error" />}
        {answer && (
          <dl className="grid grid-cols-[10rem_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs" data-testid="file-properties-rows">
            {answer.rows.map((row) => (
              <div key={row.key} className="contents" data-testid={`file-properties-row-${row.key}`}>
                <dt className="text-muted-foreground">{row.label}</dt>
                <dd className="break-words" title={row.value}>{row.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {/* U17: a visible way out (it used to close only with Esc or a click outside). */}
        <DialogFooter><Button type="button" variant="outline" onClick={onClose} data-testid="file-properties-close">Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
